import React, { useMemo } from 'react';
import { useListContext } from 'ra-core';
import { FunctionField, ListProps, NumberField, TextField } from 'react-admin';

import {
  TextInput,
  Grid,
  GridList,
  Forbidden,
  DateField,
  MoneyField,
  PercentField,
  NullableBooleanInput,
  SelectInput,
  DateRangeSelect,
} from '@components';
import { LoanContract } from '@types';
import { useExporter, useRbacEdit, useRbacList } from '@hooks';
import { LoanContractListActions } from './LoanContractListActions';
import { useLoanContractOptions } from '@pages/loan-contract/use-loan-contract-options';
import cls from './LoanContracts.module.css';
import { moneyFormatter } from '@utils';

const typeOfLoanFilterOptions = [
  { id: 'Crédito de Avío', name: 'Crédito de Avío' },
];

const Summary = () => {
  const { data, selectedIds } = useListContext<LoanContract>();
  if (!data || !selectedIds.length) return null;
  const list: LoanContract[] =
    selectedIds.length > 0
      ? (data || []).filter((i) => selectedIds.includes(i.id))
      : (data || []);

  const loanTotal = list
    .reduce((acc, curr) => acc + (curr.loanAmount || 0), 0);
  const loanAvg = +(loanTotal / list.length).toFixed(2);

  const membershipTotal = list
    .reduce((acc, curr) => acc + (curr.membership || 0), 0);
  const membershipAvg = +(membershipTotal / list.length).toFixed(2);

  const orbTotal = list
    .reduce((acc, curr) => acc + (curr.opb || 0), 0);
  const orbAvg = +(orbTotal / list.length).toFixed(2);

  const insurancePolicyTotal = list
    .reduce((acc, curr) => acc + (curr.insurancePolicyAmount || 0), 0);
  const insurancePolicyAvg = +(insurancePolicyTotal / list.length).toFixed(2);

  return (
    <>
      <span className={cls.totalBlock}>
        <span>Sum Loan Amount: {moneyFormatter.format(loanTotal)}</span>
        <span>AVG Loan Amount: {moneyFormatter.format(loanAvg)}</span>
      </span>
      <span style={{ paddingRight: 14 }} />
      <span className={cls.totalBlock}>
        <span>Sum Membership: {moneyFormatter.format(membershipTotal)}</span>
        <span>AVG Membership: {moneyFormatter.format(membershipAvg)}</span>
      </span>
      <span style={{ paddingRight: 14 }} />
      <span className={cls.totalBlock}>
        <span>Sum OPB: {moneyFormatter.format(orbTotal)}</span>
        <span>AVG OPB: {moneyFormatter.format(orbAvg)}</span>
      </span>
      <span style={{ paddingRight: 14 }} />
      <span className={cls.totalBlock}>
        <span>Sum Insurance Policy: {moneyFormatter.format(insurancePolicyTotal)}</span>
        <span>AVG Insurance Policy: {moneyFormatter.format(insurancePolicyAvg)}</span>
      </span>
    </>
  );
};

export const LoanContractList = (props: ListProps) => {
  const rbacList = useRbacList();
  const rbacEdit = useRbacEdit();
  const exporter = useExporter({
    filename: 'Loan Contracts list',
    props: {
      'id': {
        label: 'ID',
      },
      'borrower': {
        label: 'Borrower',
      },
      'creditCode': {
        label: 'Credit Code',
      },
      'isFixed': {
        label: 'Fixed contracts',
        transform: (value: boolean | null) => value ? 'Yes' : value === false ? 'No' : '',
      },
      'typeOfLoan': {
        label: 'Type of Loan',
      },
      'typeOfPayments': {
        label: 'Type of Payments',
      },
      'product': {
        label: 'Product',
      },
      'signatureDate': {
        label: 'Disbursement date',
        transform: 'date',
      },
      'signedContractAt': {
        label: 'Signature of Contract',
        transform: 'date',
      },
      'paymentDate': {
        label: 'Payment at',
        transform: 'date',
      },
      'expectedPayments': {
        label: 'Expected Payments',
      },
      'periodicity': {
        label: 'Periodicity',
      },
      'paymentPerPeriod': {
        label: 'Payment per Period',
      },
      'maxLoanAmount': {
        label: 'Max Loan Amount',
        transform: 'money',
      },
      'loanAmount': {
        label: 'Loan Amount',
        transform: 'money',
      },
      'membership': {
        label: 'Membership',
        transform: 'money',
      },
      'interestRateLoan': {
        label: 'Interest Rate',
        transform: 'percent',
      },
      'interestTax': {
        label: 'Interest Tax',
        transform: 'percent',
      },
      'moratoryInterestRateLoan': {
        label: 'Moratory Interest Rate',
        transform: 'percent',
      },
      'disposalFee': {
        label: 'Disposal Fee',
        transform: 'percent',
      },
      'disposalFeeTax': {
        label: 'Disposal Fee Tax',
        transform: 'percent',
      },
      'dpd': {
        label: 'DPD',
      },
      'daysOnAlert': {
        label: 'Days on Alert',
      },
      'typeOfClient': {
        label: 'Type of Client',
      },
      'accredited': {
        label: 'Accredited',
      },
      'userFullname': {
        label: 'Legal Representative',
      },
      'userBirthday': {
        label: 'Date of birth',
        transform: 'date',
      },
      'userRfc': {
        label: 'RFC',
      },
      'userAddress': {
        label: 'Colony',
      },
      'userState': {
        label: 'State',
      },
      'userZip': {
        label: 'ZIP',
      },
      'mainBuyerName': {
        label: 'Withholding Company',
      },
      'opb': {
        label: 'OPB',
        transform: 'money',
      },
      'cropCategory': {
        label: 'Crop Category',
      },
      'crop': {
        label: 'Crop',
      },
      'cropState': {
        label: 'Crop State',
      },
      'cropCity': {
        label: 'Crop City',
      },
      'cropZone': {
        label: 'Crop Zone',
      },
      'currentNumberOfHarvests': {
        label: 'Current Number of Harvests',
      },
      'verqorScore': {
        label: 'Verqor Score',
      },
      'creditScore': {
        label: 'Credit Score',
      },
      'debtToAssetsRatio': {
        label: 'Debt to assets ratio',
        transform: 'percent',
      },
      'returnOnEquity': {
        label: 'Return on equity',
        transform: 'percent',
      },
      'debtToEquityRatio': {
        label: 'Debt to equity Ratio',
        transform: 'percent',
      },
      'dscr': {
        label: 'DSCR',
        transform: 'percent',
      },
      'cashRatio': {
        label: 'Cash Ratio',
        transform: 'percent',
      },
      'insuranceCompany': {
        label: 'Insurance Company',
      },
      'insurancePolicyAmount': {
        label: 'Insurance Policy Amount',
        transform: 'money',
      },
      'createdAt': {
        label: 'Added at',
        transform: 'date',
      },
    },
  });
  const { borrowerOptions } = useLoanContractOptions();

  const filters = useMemo(() => ([
    <TextInput source="q" key="q" variant="outlined" label="Search" alwaysOn />,
    <NullableBooleanInput key="isFixed" source="isFixed" label="Fixed contracts" />,
    <SelectInput
      key="typeOfLoan"
      source="typeOfLoan"
      choices={typeOfLoanFilterOptions}
      label="Type of Loan"
    />,
    <DateRangeSelect
      key="dates"
      source="dates"
      label="Disbursement/Payment/Created/Signature of Contract"
      labelClass={cls.labelClass}
      alwaysOn
      choices={[
        { id: 'signatureDate', name: 'Disbursement date' },
        { id: 'paymentDate', name: 'Payment at' },
        { id: 'createdAt', name: 'Created date' },
        { id: 'signedContractAt', name: 'Signature of Contract' },
      ]}
    />,
    <SelectInput
      key="borrower"
      source="borrower"
      choices={(borrowerOptions?.options || []).map(option => ({
        id: option,
        name: option,
      }))}
      label="Borrower"
    />,
  ]), [borrowerOptions]);

  if (!rbacList) {
    return <Forbidden />;
  }

  return (
    <GridList {...props}
      editComponent={rbacEdit ? <LoanContractListActions /> : undefined}
      filters={filters}
      sort={{ field: 'signatureDate', order: 'DESC' }}
      exporter={exporter}
      summary={<Summary />}
      className={cls.list}
    >
      <Grid
        rowClick="edit"
        defaultColumns={'borrower creditCode typeOfLoan typeOfPayments product signatureDate paymentDate'.split(' ')}
        centered
      >
        <TextField source="id" label="ID" />
        <TextField source="borrower" label="Borrower" />
        <TextField source="creditCode" label="Credit Code" />
        <TextField source="typeOfLoan" label="Type of Loan" />
        <FunctionField
          source="isFixed"
          label="Fixed contracts"
          render={(record: any) => (
            <div>{record.isFixed ? 'Yes' : record.isFixed === false ? 'No' : ''}</div>
          )}
        />
        <TextField source="typeOfPayments" label="Type of Payments" />
        <TextField source="product" label="Product" />
        <DateField source="signatureDate" label="Disbursement date" />
        <DateField source="signedContractAt" label="Signature of Contract" />
        <DateField source="paymentDate" label="Payment at" />
        <NumberField source="expectedPayments" label="Expected Payments" />
        <NumberField source="periodicity" label="Periodicity" />
        <NumberField source="paymentPerPeriod" label="Payment per Period" />
        <MoneyField source="maxLoanAmount" label="Max loan amount" />
        <MoneyField source="loanAmount" label="Loan Amount" />
        <MoneyField source="membership" label="Membership" />
        <PercentField source="interestRateLoan" label="Interest Rate" />
        <PercentField source="interestTax" label="Interest Tax" />
        <PercentField source="moratoryInterestRateLoan" label="Moratory Interest Rate" />
        <PercentField source="moratoryTax" label="Moratory Tax" />
        <PercentField source="disposalFee" label="Disposal Fee" />
        <PercentField source="disposalFeeTax" label="Disposal Fee Tax" />
        <TextField source="dpd" label="DPD" />
        <NumberField source="daysOnAlert" label="Days on Alert" />
        <TextField source="typeOfClient" label="Type of Client" />
        <TextField source="accredited" label="Accredited" />
        <TextField source="userFullname" label="Legal Representative" />
        <DateField source="userBirthday" label="Date of birth" />
        <TextField source="userRfc" label="RFC" />
        <TextField source="userAddress" label="Colony" />
        <TextField source="userState" label="State" />
        <TextField source="userZip" label="ZIP" />
        <TextField source="mainBuyerName" label="Withholding Company" />
        <MoneyField source="opb" label="OPB" />
        <TextField source="cropCategory" label="Crop Category" />
        <TextField source="crop" label="Crop" />
        <TextField source="cropState" label="Crop State" />
        <TextField source="cropCity" label="Crop City" />
        <TextField source="cropZone" label="Crop Zone" />
        <NumberField source="currentNumberOfHarvests" label="Current Number of harvests" />
        <NumberField source="verqorScore" label="Verqor Score" />
        <NumberField source="creditScore" label="Credit Score" />
        <PercentField source="debtToAssetsRatio" label="Debt to assets ratio" />
        <PercentField source="returnOnEquity" label="Return on equity" />
        <PercentField source="debtToEquityRatio" label="Debt to equity Ratio" />
        <PercentField source="dscr" label="DSCR" />
        <PercentField source="cashRatio" label="Cash Ratio" />
        <TextField source="insuranceCompany" label="Insurance Company" />
        <MoneyField source="insurancePolicyAmount" label="Insurance Policy Amount" />
        <DateField source="createdAt" label="Created date" />
      </Grid>
    </GridList>
  );
};
